import { useEffect, useState } from "react";
import { api } from "@/hub/lib/api";
import { BookUser, Plus, Upload, Search, Trash2, Loader2, Ban, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Contacts() {
  const [data, setData] = useState({ contacts: [], total: 0, mailable: 0 });
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", phone: "", tags: ["professional"] });
  const [importText, setImportText] = useState("");
  const [importTag, setImportTag] = useState("professional");
  const [busy, setBusy] = useState(false);
  const role = localStorage.getItem("lush_role");

  const load = () => api.contacts(q, tag).then(setData);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, tag]);

  const add = async () => {
    setBusy(true);
    try {
      await api.contactCreate(form);
      toast.success("Contact added");
      setAddOpen(false);
      setForm({ email: "", name: "", phone: "", tags: ["professional"] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    const rows = importText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [email, ...rest] = l.split(/[,;\t]/);
      return { email: email.trim(), name: rest.join(" ").trim() };
    });
    if (!rows.length) return toast.error("Paste at least one email");
    setBusy(true);
    try {
      const r = await api.contactImport({ rows, tag: importTag });
      toast.success(`Imported: ${r.added} added, ${r.updated} updated, ${r.skipped} skipped`);
      setImportOpen(false);
      setImportText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const toggleTag = (t) => setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  return (
    <div data-testid="contacts-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-forest flex items-center gap-2"><BookUser className="h-6 w-6 text-terracotta" /> Contacts</h1>
          <p className="text-sm text-stone-500">{data.total} total · <span className="text-forest font-medium">{data.mailable} mailable</span> (consented, not unsubscribed)</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="contacts-import-btn" variant="outline" className="rounded-sm border-stone-300" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4 mr-1.5" /> Import List
          </Button>
          <Button data-testid="contacts-add-btn" className="rounded-sm bg-forest hover:bg-forest-hover" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Add Contact
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email..."
            className="w-full bg-white border border-stone-200 rounded-sm pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-forest" />
        </div>
        <div className="flex gap-2" data-testid="contacts-tag-filter">
          {["", "professional", "individual"].map((t) => (
            <button key={t} onClick={() => setTag(t)}
              className={`px-4 py-2 text-xs uppercase tracking-wider rounded-sm border transition-colors ${tag === t ? "bg-forest text-white border-forest" : "bg-white text-stone-500 border-stone-200 hover:border-forest"}`}>
              {t === "" ? "All" : t + "s"}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm divide-y divide-stone-100" data-testid="contacts-list">
        {data.contacts.length === 0 && (
          <div className="p-10 text-center text-sm text-stone-400">No contacts yet — add one or import your existing list (one email per line).</div>
        )}
        {data.contacts.map((c) => (
          <div key={c.id} data-testid={`contact-row-${c.id}`} className="p-4 flex items-center gap-4">
            <div className="h-9 w-9 shrink-0 rounded-full bg-forest/10 text-forest flex items-center justify-center text-sm font-semibold">
              {(c.name || c.email).charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-stone-800 truncate">{c.name || "—"}</div>
              <div className="text-xs text-stone-400 truncate">{c.company ? `${c.company} · ` : ""}{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
            </div>
            <div className="hidden md:flex gap-1.5">
              {c.tags?.map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-stone-100 text-stone-500">{t}</span>
              ))}
            </div>
            <div className="text-[11px] text-stone-400 hidden lg:block w-28 truncate">{c.source}</div>
            {c.unsubscribed ? (
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-500"><Ban className="h-3 w-3" /> Unsubscribed</span>
            ) : c.marketing_consent ? (
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-600"><CheckCircle2 className="h-3 w-3" /> Consented</span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider text-stone-400">No consent</span>
            )}
            {role === "Admin" && (
              <button data-testid={`contact-delete-${c.id}`} onClick={async () => { if (window.confirm(`Delete ${c.email}?`)) { await api.contactDelete(c.id); load(); } }}
                className="text-stone-300 hover:text-terracotta transition-colors"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="contact-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Contact</DialogTitle>
            <DialogDescription className="text-xs">Added contacts can receive proposals; only consented ones get promotions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input data-testid="contact-email-input" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-stone-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-forest" />
            <input data-testid="contact-name-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-stone-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-forest" />
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full border border-stone-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-forest" />
            <div className="flex gap-2">
              {["professional", "individual"].map((t) => (
                <button key={t} type="button" onClick={() => toggleTag(t)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border ${form.tags.includes(t) ? "bg-forest text-white border-forest" : "bg-white text-stone-500 border-stone-200"}`}>
                  {t}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs text-stone-500">
              <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="accent-[#1C3F35]" />
              Consented to receive promotions
            </label>
            <Button data-testid="contact-save-btn" onClick={add} disabled={busy || !form.email} className="w-full rounded-sm bg-forest hover:bg-forest-hover">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Save Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="contact-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Import Contact List</DialogTitle>
            <DialogDescription className="text-xs">One per line: <code>email, name</code> — only import people who agreed to hear from you.</DialogDescription>
          </DialogHeader>
          <textarea data-testid="contact-import-textarea" rows={8} value={importText} onChange={(e) => setImportText(e.target.value)}
            placeholder={"saman@company.lk, Saman Perera\nnadeesha@studio.com, Nadeesha"}
            className="w-full border border-stone-200 rounded-sm px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-forest" />
          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-2">
              {["professional", "individual"].map((t) => (
                <button key={t} type="button" onClick={() => setImportTag(t)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border ${importTag === t ? "bg-forest text-white border-forest" : "bg-white text-stone-500 border-stone-200"}`}>
                  {t}s
                </button>
              ))}
            </div>
            <Button data-testid="contact-import-run" onClick={doImport} disabled={busy} className="rounded-sm bg-forest hover:bg-forest-hover">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Import
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
